class WithdrawalForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('[data-entry]');
    this.review = this.querySelector('[data-review]');
    this.confirmButton = this.querySelector('[data-confirm]');
    this.error = this.querySelector('[data-error]');
    this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
    this.requestKey = crypto.randomUUID();
    this.form.addEventListener('change', () => {
      const selected = this.form.elements.scope.value === 'items';
      this.querySelector('[data-items-field]').hidden = !selected;
      this.form.elements.items.required = selected;
    });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.showReview(); });
    this.querySelector('[data-edit]').addEventListener('click', () => this.edit());
    this.confirmButton.addEventListener('click', () => this.submit());
    this.querySelector('[data-print]').addEventListener('click', () => window.print());
    try {
      const endpoint = new URL(this.dataset.endpoint);
      const local = ['localhost', '127.0.0.1'].includes(endpoint.hostname) && ['localhost', '127.0.0.1'].includes(location.hostname);
      if (endpoint.protocol !== 'https:' && !local) throw new Error('invalid_endpoint');
      if (!local && !this.dataset.siteKey) throw new Error('missing_site_key');
      this.endpoint = endpoint.origin;
      this.local = local;
      this.form.hidden = false;
    } catch {
      this.form.hidden = true;
      this.querySelector('[data-unavailable]').hidden = false;
    }
  }

  edit() {
    if (this.sending) return;
    this.requestKey = crypto.randomUUID();
    this.review.hidden = true;
    this.form.hidden = false;
    this.error.hidden = true;
    this.form.elements.name.focus();
  }

  showReview() {
    if (!this.endpoint || !this.form.reportValidity()) return;
    const fields = new FormData(this.form);
    this.data = Object.fromEntries(fields);
    for (const key of ['name', 'email', 'orderReference', 'items']) this.data[key] = (this.data[key] || '').trim();
    this.data.locale = this.dataset.locale === 'de' ? 'de' : 'en';
    for (const element of this.querySelectorAll('[data-value]')) {
      const key = element.dataset.value;
      element.textContent = key === 'scope' ? (this.data.scope === 'whole' ? this.messages.whole : this.data.items) : this.data[key];
    }
    this.declaration = (this.data.scope === 'whole' ? this.messages.wholeDeclaration : this.messages.itemsDeclaration)
      .replace(/\[(order|items)\]/g, (_placeholder, field) => field === 'order' ? this.data.orderReference : this.data.items);
    this.querySelector('[data-declaration]').textContent = this.declaration;
    this.form.hidden = true;
    this.review.hidden = false;
    this.error.hidden = true;
    this.focusPanel('[data-review-heading]');
    if (!this.local) this.renderChallenge();
  }

  async renderChallenge() {
    this.confirmButton.disabled = true;
    this.token = '';
    try {
      if (!window.turnstile) {
        if (!window.mysteariumTurnstileReady) window.mysteariumTurnstileReady = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.async = true;
          script.onload = resolve;
          script.onerror = () => { window.mysteariumTurnstileReady = null; script.remove(); reject(new Error('challenge_load')); };
          document.head.appendChild(script);
        });
        await window.mysteariumTurnstileReady;
      }
      if (this.widget !== undefined) window.turnstile.remove(this.widget);
      this.widget = window.turnstile.render(this.querySelector('[data-challenge]'), {
        sitekey: this.dataset.siteKey,
        action: 'withdrawal',
        language: this.data.locale,
        callback: token => { this.token = token; this.confirmButton.disabled = false; },
        'expired-callback': () => { this.token = ''; this.confirmButton.disabled = true; },
        'error-callback': () => this.showError(this.messages.challenge),
      });
    } catch { this.showError(this.messages.challenge); }
  }

  focusPanel(selector) {
    const heading = this.querySelector(selector);
    heading.focus({ preventScroll: true });
    heading.scrollIntoView({ block: 'start' });
  }

  showError(text) {
    this.error.textContent = text;
    this.error.hidden = false;
  }

  async submit() {
    if (this.sending || (!this.local && !this.token)) return;
    this.sending = true;
    this.confirmButton.disabled = true;
    this.confirmButton.textContent = this.messages.sending;
    this.error.hidden = true;
    try {
      const response = await fetch(this.endpoint + '/withdrawals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...this.data, requestKey: this.requestKey, confirmed: true, turnstileToken: this.token || '' }),
        signal: AbortSignal.timeout(20000),
      });
      const result = await response.json();
      if (!response.ok || !result.reference || !result.receivedAt) throw new Error('submission_failed');
      const date = new Intl.DateTimeFormat(this.data.locale, { timeZone: 'Europe/Berlin', dateStyle: 'long', timeStyle: 'long' }).format(new Date(result.receivedAt));
      this.querySelector('[data-receipt-details]').textContent = this.messages.receipt.replace('[reference]', result.reference).replace('[date]', date) + '\n\n' + this.declaration;
      this.review.hidden = true;
      this.querySelector('[data-receipt]').hidden = false;
      this.focusPanel('[data-receipt-heading]');
    } catch {
      this.showError(this.messages.error);
      if (!this.local) await this.renderChallenge();
    } finally {
      this.sending = false;
      this.confirmButton.textContent = this.messages.confirm;
      this.confirmButton.disabled = !this.local && !this.token;
    }
  }
}

if (!customElements.get('withdrawal-form')) customElements.define('withdrawal-form', WithdrawalForm);
